import React from "react";
import dayjs from "dayjs";
import "./TaskQuickOptions.css";

const normalizeId = (value) => {
    if (value == null || value === "") return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

export const getTaskDateShortcuts = (referenceDate = dayjs()) => {
    const today = referenceDate.startOf("day").hour(23).minute(59).second(0).millisecond(0);
    let eow = today.day(5);
    if (today.day() > 5) {
        eow = today.add(1, "week").day(5);
    }

    let nextMonday = today.day(8);
    if (today.day() === 0) {
        nextMonday = today.add(1, "day");
    }

    return {
        today,
        tomorrow: today.add(1, "day"),
        eow,
        nextMonday,
    };
};

const DATE_SHORTCUTS = [
    { key: "today", label: "Today" },
    { key: "tomorrow", label: "Tomorrow" },
    { key: "eow", label: "EOW" },
    { key: "nextMonday", label: "Next Mon" },
];

export function TaskDateShortcutChips({ onSelect, className = "", disabled = false }) {
    const dates = getTaskDateShortcuts();

    return (
        <span className={`task-quick-options task-quick-options--dates ${className}`.trim()}>
            {DATE_SHORTCUTS.map((shortcut) => (
                <button
                    key={shortcut.key}
                    type="button"
                    className="task-quick-chip"
                    disabled={disabled}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onSelect?.(dates[shortcut.key], shortcut.key)}
                >
                    {shortcut.label}
                </button>
            ))}
        </span>
    );
}

export const getUserId = (user) => normalizeId(user?.id ?? user?.user_id ?? user?.userId);

export const getCurrentUserId = (currentUser) => normalizeId(
    currentUser?.id ?? currentUser?.user_id ?? currentUser?.userId
);

export const getUserDisplayName = (user) => {
    if (!user) return "";
    const fullName = [
        user.first_name || user.firstName,
        user.last_name || user.lastName,
    ].filter(Boolean).join(" ").trim();
    return user.name || fullName || user.email?.split("@")[0] || `User ${getUserId(user) || ""}`.trim();
};

const isAssignableUser = (user) => Boolean(user) && user.is_active !== false;

export const getCurrentUserManagerId = (currentUser, users = []) => {
    const directManagerId = normalizeId(
        currentUser?.manager_id ??
        currentUser?.managerId ??
        currentUser?.manager?.id ??
        currentUser?.employment?.mainManagerId
    );
    if (directManagerId) return directManagerId;

    const currentUserId = getCurrentUserId(currentUser);
    const matchingUser = users.find((user) => getUserId(user) === currentUserId);
    return normalizeId(
        matchingUser?.manager_id ??
        matchingUser?.managerId ??
        matchingUser?.manager?.id
    );
};

const getTaskAssigneeId = (task) => normalizeId(
    task?.default_assignee_id ??
    task?.default_assignee?.id ??
    task?.assignee_id ??
    task?.assignee?.id
);

const getTaskVerifierId = (task) => normalizeId(
    task?.verifier_id ??
    task?.verifier?.id
);

export const getTaskClientId = (task) => normalizeId(
    task?.client_id ??
    task?.client?.id ??
    task?.project?.client_id ??
    task?.project?.client?.id
);

export const RECENT_ASSIGNMENT_TASK_PARAMS = {
    taskType: "delegated",
    includeCompleted: true,
    limit: 200,
};

const countUserIdsFromTasks = (tasks = [], getUserIdFromTask, { excludeIds = [] } = {}) => {
    const excluded = new Set(excludeIds.map(normalizeId).filter(Boolean));
    const counts = new Map();

    tasks.forEach((task) => {
        const userId = getUserIdFromTask(task);
        if (!userId || excluded.has(userId)) return;
        counts.set(userId, (counts.get(userId) || 0) + 1);
    });

    return counts;
};

const resolveFrequentUsers = (counts, users = [], limit = 3) => {
    const usersById = new Map(users.map((user) => [getUserId(user), user]));

    return Array.from(counts.entries())
        .map(([userId, count]) => ({ user: usersById.get(userId), userId, count }))
        .filter((entry) => isAssignableUser(entry.user))
        .sort((a, b) => b.count - a.count || getUserDisplayName(a.user).localeCompare(getUserDisplayName(b.user)))
        .slice(0, limit)
        .map((entry) => entry.user);
};

export const getFrequentAssigneeUsers = (tasks = [], users = [], { excludeIds = [], limit = 3 } = {}) => (
    resolveFrequentUsers(
        countUserIdsFromTasks(tasks, getTaskAssigneeId, { excludeIds }),
        users,
        limit
    )
);

export const getFrequentVerifierUsers = (tasks = [], users = [], { excludeIds = [], limit = 3 } = {}) => (
    resolveFrequentUsers(
        countUserIdsFromTasks(tasks, getTaskVerifierId, { excludeIds }),
        users,
        limit
    )
);

export const getFrequentClients = (tasks = [], clients = [], { limit = 3 } = {}) => {
    const counts = countUserIdsFromTasks(tasks, getTaskClientId);
    const clientsById = new Map(clients.map((client) => [normalizeId(client?.id), client]));

    const resolveClient = (clientId) => {
        const fromList = clientsById.get(clientId);
        if (fromList) return fromList;

        for (const task of tasks) {
            if (getTaskClientId(task) !== clientId) continue;
            if (task?.client?.id === clientId) return task.client;
            if (task?.project?.client?.id === clientId) return task.project.client;
        }

        return { id: clientId, name: `Client ${clientId}` };
    };

    return Array.from(counts.entries())
        .map(([clientId, count]) => ({ client: resolveClient(clientId), clientId, count }))
        .sort((a, b) => b.count - a.count || String(a.client.name).localeCompare(String(b.client.name)))
        .slice(0, limit)
        .map((entry) => entry.client);
};

const buildShortcut = (key, label, user) => {
    const userId = getUserId(user);
    if (!userId) return null;
    return { key, label, userId };
};

export function TaskAssigneeShortcutChips({
    users = [],
    currentUser,
    frequentUsers = [],
    selectedUserId,
    excludedUserIds = [],
    disabledUserIds = [],
    onSelect,
    className = "",
    disabled = false,
}) {
    const currentUserId = getCurrentUserId(currentUser);
    const managerId = getCurrentUserManagerId(currentUser, users);
    const usersById = new Map(users.map((user) => [getUserId(user), user]));
    if (currentUserId && currentUser && !usersById.has(currentUserId)) {
        usersById.set(currentUserId, currentUser);
    }
    const currentUserManagerId = getUserId(currentUser?.manager);
    if (currentUserManagerId && currentUser?.manager && !usersById.has(currentUserManagerId)) {
        usersById.set(currentUserManagerId, currentUser.manager);
    }
    const managerUser = managerId
        ? (
            usersById.get(managerId)
            || users.find((user) => getUserId(user) === managerId)
            || { id: managerId }
        )
        : null;

    const excluded = new Set(excludedUserIds.map(normalizeId).filter(Boolean));
    const disabledIds = new Set(disabledUserIds.map(normalizeId).filter(Boolean));
    const seen = new Set();
    const shortcuts = [
        buildShortcut("me", "Me", usersById.get(currentUserId) || currentUser),
        buildShortcut("manager", "My Manager", managerUser),
        ...frequentUsers.map((user) => buildShortcut(`frequent-${getUserId(user)}`, getUserDisplayName(user), user)),
    ].filter(Boolean).filter((shortcut) => {
        if (excluded.has(shortcut.userId) || seen.has(shortcut.userId)) return false;
        if (!isAssignableUser(usersById.get(shortcut.userId))) return false;
        seen.add(shortcut.userId);
        return true;
    });

    if (shortcuts.length === 0) return null;

    return (
        <span className={`task-quick-options task-quick-options--assignees ${className}`.trim()}>
            {shortcuts.map((shortcut) => (
                <button
                    key={shortcut.key}
                    type="button"
                    className={`task-quick-chip ${Number(selectedUserId) === Number(shortcut.userId) ? "is-selected" : ""}`}
                    disabled={disabled || disabledIds.has(shortcut.userId)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onSelect?.(shortcut.userId)}
                >
                    {shortcut.label}
                </button>
            ))}
        </span>
    );
}

export function TaskClientShortcutChips({
    clients = [],
    frequentClients = [],
    selectedClientId,
    onSelect,
    className = "",
    disabled = false,
}) {
    const clientsById = new Map(clients.map((client) => [normalizeId(client?.id), client]));
    const seen = new Set();
    const shortcuts = frequentClients
        .map((client) => {
            const clientId = normalizeId(client?.id);
            if (!clientId) return null;
            const label = client?.name || clientsById.get(clientId)?.name || `Client ${clientId}`;
            return { key: `frequent-${clientId}`, label, clientId };
        })
        .filter(Boolean)
        .filter((shortcut) => {
            if (seen.has(shortcut.clientId)) return false;
            seen.add(shortcut.clientId);
            return true;
        });

    if (shortcuts.length === 0) return null;

    return (
        <span className={`task-quick-options task-quick-options--clients ${className}`.trim()}>
            {shortcuts.map((shortcut) => (
                <button
                    key={shortcut.key}
                    type="button"
                    className={`task-quick-chip ${Number(selectedClientId) === Number(shortcut.clientId) ? "is-selected" : ""}`}
                    disabled={disabled}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onSelect?.(shortcut.clientId)}
                >
                    {shortcut.label}
                </button>
            ))}
        </span>
    );
}
